import type {
  SportsDataProvider, ProviderFixture, ProviderLineup, ProviderHeadToHead,
  ProviderTeamStats, ProviderPlayerStats,
  ProviderMatchDetails, ProviderInjury, ProviderSuspension, ProviderCard,
  ProviderStandings, ProviderMatchEvent,
  ProviderFixturePlayers, ProviderFixturePlayersTeam, ProviderPlayerMatchStat,
} from "./types";
import { DemoProvider } from "./DemoProvider";
import { apiFootballCall } from "@/lib/apiFootball.functions";
import { resolveH2HPair } from "@/lib/sports/teamProviderMap";

/**
 * API-Football provider scaffold.
 *
 * The build never requires an API key — when `VITE_API_FOOTBALL_KEY` is
 * absent, every call gracefully falls back to the DemoProvider so the rest
 * of the platform keeps working. When the key is present, calls are routed
 * through https://v3.football.api-sports.io with rate limiting and retries.
 */

const MAX_RETRIES = 2;

/** Server-side proxy keeps API_FOOTBALL_KEY out of the client bundle. */
async function call<T>(path: string): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const r = await apiFootballCall({ data: { path } });
      if (!r.ok) throw new Error(r.error ?? `api-football ${r.status}`);
      return r.data as T;
    } catch (e) {
      lastErr = e;
      if (e instanceof Error && e.message === "API_FOOTBALL_KEY_MISSING") throw e;
      await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
    }
  }
  throw lastErr;
}

/** api-football status.short → internal status. */
function mapStatus(short: string | undefined): "SCHEDULED" | "LIVE" | "FINISHED" {
  const live = ["1H", "HT", "2H", "ET", "BT", "P", "LIVE", "INT"];
  const finished = ["FT", "AET", "PEN", "AWD", "WO"];
  if (short && live.includes(short)) return "LIVE";
  if (short && finished.includes(short)) return "FINISHED";
  return "SCHEDULED";
}

interface AfFixtureItem {
  fixture?: {
    id?: number; date?: string;
    status?: { short?: string; long?: string; elapsed?: number | null; extra?: number | null };
    venue?: { name?: string };
    referee?: string | null;
  };
  league?: { id?: number; name?: string; season?: number; logo?: string | null; round?: string | null };
  teams?: {
    home?: { id?: number; name?: string; logo?: string | null };
    away?: { id?: number; name?: string; logo?: string | null };
  };
  goals?: { home?: number | null; away?: number | null };
  score?: { penalty?: { home?: number | null; away?: number | null } };
}

interface AfLineupPlayer {
  player?: {
    id?: number;
    name?: string;
    number?: number;
    pos?: string;
    grid?: string | null;
    captain?: boolean;
    photo?: string | null;
  };
}
interface AfLineupTeamBlock {
  team?: { id?: number; name?: string; logo?: string | null };
  coach?: { id?: number; name?: string; photo?: string | null };
  formation?: string | null;
  startXI?: AfLineupPlayer[];
  substitutes?: AfLineupPlayer[];
}
interface AfEventItem {
  time?: { elapsed?: number | null; extra?: number | null };
  team?: { id?: number; name?: string };
  player?: { id?: number | null; name?: string | null };
  assist?: { id?: number | null; name?: string | null };
  type?: string;
  detail?: string;
  comments?: string | null;
}
interface AfStatBlock {
  team?: { id?: number; name?: string };
  statistics?: Array<{ type?: string; value?: number | string | null }>;
}

function toNumber(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v.replace("%", ""));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function normalizeFixture(item: AfFixtureItem, idx = 0): ProviderFixture {
  const fx = item.fixture ?? {};
  const t = item.teams ?? {};
  const lg = item.league ?? {};
  return {
    id: fx.id != null ? String(fx.id) : String(idx),
    homeTeamId: t.home?.id != null ? String(t.home.id) : "?",
    awayTeamId: t.away?.id != null ? String(t.away.id) : "?",
    homeTeam: t.home?.name ?? "?",
    awayTeam: t.away?.name ?? "?",
    competitionId: lg.id != null ? String(lg.id) : "fifa-wc-2026",
    kickoff: fx.date ?? new Date().toISOString(),
    status: mapStatus(fx.status?.short),
  };
}

export const ApiFootballProvider: SportsDataProvider = {
  name: "api_football",
  async getFixtures(): Promise<ProviderFixture[]> {
    try {
      const data = await call<{ response: AfFixtureItem[] }>("/fixtures?live=all");
      return (data.response ?? []).map((item, i) => normalizeFixture(item, i));
    } catch {
      return DemoProvider.getFixtures();
    }
  },
  async getLineups(fixtureId): Promise<ProviderLineup> {
    // Real Data Phase 3 — official lineups only. On empty response or
    // provider failure we surface an empty payload so the UI renders the
    // lifecycle placeholder ("Official lineups unavailable") instead of
    // fabricating a starting XI.
    const emptyPayload = (): ProviderLineup => ({
      fixtureId,
      official: false,
      provider: "api_football",
      home: null,
      away: null,
      homePlayers: [],
      awayPlayers: [],
    });
    try {
      const data = await call<{ response: AfLineupTeamBlock[] }>(
        `/fixtures/lineups?fixture=${fixtureId}`,
      );
      const blocks = data.response ?? [];
      if (blocks.length < 1) return emptyPayload();
      const mapPlayer = (p: AfLineupPlayer) => ({
        id: p.player?.id ?? 0,
        name: p.player?.name ?? "?",
        number: p.player?.number ?? 0,
        position: p.player?.pos ?? "",
        grid: p.player?.grid ?? null,
        captain: p.player?.captain ?? false,
        photo: p.player?.photo ?? null,
      });
      const mapTeam = (b?: AfLineupTeamBlock) => {
        if (!b) return null;
        return {
          teamId: b.team?.id != null ? String(b.team.id) : null,
          teamName: b.team?.name ?? "?",
          teamLogo: b.team?.logo ?? null,
          formation: b.formation ?? null,
          coach: b.coach?.name ?? null,
          coachPhoto: b.coach?.photo ?? null,
          startXI: (b.startXI ?? []).map(mapPlayer),
          substitutes: (b.substitutes ?? []).map(mapPlayer),
        };
      };
      const home = mapTeam(blocks[0]);
      const away = mapTeam(blocks[1]);
      return {
        fixtureId,
        official: true,
        provider: "api_football",
        home,
        away,
        homePlayers: home?.startXI ?? [],
        awayPlayers: away?.startXI ?? [],
      };
    } catch {
      return emptyPayload();
    }
  },
  async getHeadToHead(homeTeamId, awayTeamId, competitionId): Promise<ProviderHeadToHead> {
    // QA-DATA-INSIGHTS-02 — resolve FIFA codes (ARG/MEX) to api-football
    // numeric IDs before calling upstream. Without this, every request to
    // /fixtures/headtohead returns an empty response.
    const resolved = await resolveH2HPair(homeTeamId, awayTeamId);
    if (resolved.missing.length > 0) {
      console.warn("[H2H_PROVIDER_NO_MAPPING]", { missing: resolved.missing });
      return {
        homeTeamId, awayTeamId,
        competitionId: competitionId ?? "fifa-wc-2026",
        matches: [],
        homeProviderId: resolved.home.api_football_team_id,
        awayProviderId: resolved.away.api_football_team_id,
        homeProviderName: resolved.home.api_football_name,
        awayProviderName: resolved.away.api_football_name,
        status: "no_mapping",
        missingCodes: resolved.missing,
      };
    }
    const homeId = resolved.home.api_football_team_id!;
    const awayId = resolved.away.api_football_team_id!;
    try {
      const data = await call<{ response: AfFixtureItem[] }>(`/fixtures/headtohead?h2h=${homeId}-${awayId}`);
      console.log("[H2H_PROVIDER_RAW]", {
        homeTeamId, awayTeamId, homeId, awayId,
        responseCount: data.response?.length ?? 0,
      });
      const matches = (data.response ?? []).map((item) => {
        const t = item.teams ?? {};
        const g = item.goals ?? {};
        const hg = typeof g.home === "number" ? g.home : 0;
        const ag = typeof g.away === "number" ? g.away : 0;
        return {
          date: item.fixture?.date ?? new Date().toISOString(),
          competition: item.league?.name ?? "",
          // Normalize to the caller's FIFA codes so computeMetrics() in the
          // engine can keep using string equality on homeTeamId.
          homeTeamId: t.home?.id === homeId ? homeTeamId : awayTeamId,
          awayTeamId: t.away?.id === homeId ? homeTeamId : awayTeamId,
          homeTeam: t.home?.name ?? homeTeamId,
          awayTeam: t.away?.name ?? awayTeamId,
          homeGoals: hg, awayGoals: ag,
          winner: (hg === ag ? "draw" : hg > ag ? "home" : "away") as "home" | "away" | "draw",
        };
      });
      return {
        homeTeamId, awayTeamId,
        competitionId: competitionId ?? "fifa-wc-2026",
        matches,
        homeProviderId: homeId,
        awayProviderId: awayId,
        homeProviderName: resolved.home.api_football_name,
        awayProviderName: resolved.away.api_football_name,
        status: matches.length > 0 ? "ok" : "no_data",
      };
    } catch (e) {
      // QA-DATA-INSIGHTS-01: NEVER substitute synthetic Demo data here — that
      // produces fake "México 0 / Empates 0 / Argentina 0" style placeholders.
      // Surface an empty payload so the UI can render "Datos históricos no
      // disponibles" with an honest provenance badge.
      console.warn("[H2H_PROVIDER_ERR]", { reason: e instanceof Error ? e.message : "unknown" });
      return {
        homeTeamId, awayTeamId,
        competitionId: competitionId ?? "fifa-wc-2026",
        matches: [],
        homeProviderId: homeId,
        awayProviderId: awayId,
        homeProviderName: resolved.home.api_football_name,
        awayProviderName: resolved.away.api_football_name,
        status: "error",
      };
    }
  },
  async getTeamStats(teamId): Promise<ProviderTeamStats> {
    return DemoProvider.getTeamStats(teamId);
  },
  async getPlayerStats(playerId): Promise<ProviderPlayerStats> {
    return DemoProvider.getPlayerStats(playerId);
  },
  async getMatchDetails(fixtureId): Promise<ProviderMatchDetails> {
    try {
      const data = await call<{ response: AfFixtureItem[] }>(`/fixtures?id=${fixtureId}`);
      const item = (data.response ?? [])[0];
      if (!item) return DemoProvider.getMatchDetails(fixtureId);
      const t = item.teams ?? {};
      const fx = item.fixture ?? {};
      const g = item.goals ?? {};
      const pen = item.score?.penalty ?? {};
      const league = item.league ?? {};
      return {
        fixtureId,
        homeTeam: t.home?.name ?? "?",
        awayTeam: t.away?.name ?? "?",
        competition: league.name ?? "FIFA World Cup 2026",
        status: mapStatus(fx.status?.short),
        minute: typeof fx.status?.elapsed === "number" ? fx.status.elapsed : undefined,
        scoreHome: typeof g.home === "number" ? g.home : undefined,
        scoreAway: typeof g.away === "number" ? g.away : undefined,
        venue: fx.venue?.name,
        statusShort: fx.status?.short ?? null,
        statusLong: fx.status?.long ?? null,
        extraMinute: typeof fx.status?.extra === "number" ? fx.status.extra : null,
        penaltyHome: typeof pen.home === "number" ? pen.home : null,
        penaltyAway: typeof pen.away === "number" ? pen.away : null,
        kickoff: fx.date ?? null,
        homeTeamId: t.home?.id != null ? String(t.home.id) : null,
        awayTeamId: t.away?.id != null ? String(t.away.id) : null,
        homeLogo: t.home?.logo ?? null,
        awayLogo: t.away?.logo ?? null,
        competitionLogo: league.logo ?? null,
        round: league.round ?? null,
        referee: fx.referee ?? null,
        official: true,
        provider: "api_football",
      };
    } catch {
      return DemoProvider.getMatchDetails(fixtureId);
    }
  },
  async getInjuries(teamId): Promise<ProviderInjury[]> {
    return DemoProvider.getInjuries(teamId);
  },
  async getSuspensions(teamId): Promise<ProviderSuspension[]> {
    return DemoProvider.getSuspensions(teamId);
  },
  async getCards(fixtureId): Promise<ProviderCard[]> {
    try {
      const data = await call<{ response: AfEventItem[] }>(`/fixtures/events?fixture=${fixtureId}`);
      const events = data.response ?? [];
      return events
        .filter((e) => (e.type ?? "").toLowerCase() === "card")
        .map((e) => ({
          playerId: e.player?.id ?? 0,
          playerName: e.player?.name ?? "?",
          team: e.team?.name ?? "?",
          type: (e.detail ?? "").toLowerCase().includes("red") ? "red" as const : "yellow" as const,
          minute: (e.time?.elapsed ?? 0) + (e.time?.extra ?? 0),
        }));
    } catch {
      return DemoProvider.getCards(fixtureId);
    }
  },
  async getStats(fixtureId): Promise<Record<string, number | string>> {
    try {
      const data = await call<{ response: AfStatBlock[] }>(`/fixtures/statistics?fixture=${fixtureId}`);
      const blocks = data.response ?? [];
      if (blocks.length < 1) {
        return { __source: "api_football" };
      }
      const pick = (b: AfStatBlock | undefined, type: string): number => {
        const row = b?.statistics?.find((s) => (s.type ?? "").toLowerCase() === type.toLowerCase());
        return toNumber(row?.value);
      };
      const home = blocks[0];
      const away = blocks[1];
      return {
        possessionHome: pick(home, "Ball Possession"),
        possessionAway: pick(away, "Ball Possession"),
        shotsHome: pick(home, "Total Shots"),
        shotsAway: pick(away, "Total Shots"),
        xgHome: pick(home, "expected_goals"),
        xgAway: pick(away, "expected_goals"),
        cornersHome: pick(home, "Corner Kicks"),
        cornersAway: pick(away, "Corner Kicks"),
        __source: "api_football",
      };
    } catch {
      return { __source: "api_football" };
    }
  },
  async getStandings(competitionId): Promise<ProviderStandings> {
    try {
      // competitionId is expected to be a numeric api-football league id; default season 2026.
      const data = await call<{ response: Array<{ league?: { standings?: Array<Array<Record<string, unknown>>> } }> }>(
        `/standings?league=${competitionId}&season=2026`,
      );
      const group = data.response?.[0]?.league?.standings?.[0];
      if (!group || !Array.isArray(group)) return DemoProvider.getStandings(competitionId);
      const rows = group.map((r, i) => {
        const team = (r.team as { id?: number; name?: string } | undefined) ?? {};
        const all = (r.all as { played?: number; win?: number; draw?: number; lose?: number; goals?: { for?: number; against?: number } } | undefined) ?? {};
        return {
          rank: typeof r.rank === "number" ? r.rank : i + 1,
          teamId: team.id != null ? String(team.id) : String(i),
          teamName: team.name ?? "?",
          played: all.played ?? 0,
          wins: all.win ?? 0,
          draws: all.draw ?? 0,
          losses: all.lose ?? 0,
          points: typeof r.points === "number" ? r.points : 0,
          goalsFor: all.goals?.for ?? 0,
          goalsAgainst: all.goals?.against ?? 0,
        };
      });
      return { competitionId, rows };
    } catch {
      return DemoProvider.getStandings(competitionId);
    }
  },
  async getEvents(fixtureId): Promise<ProviderMatchEvent[]> {
    return getOfficialEvents(fixtureId);
  },
  async getFixturePlayers(fixtureId): Promise<ProviderFixturePlayers> {
    return getOfficialFixturePlayers(fixtureId);
  },
};

/** Official match events fetcher — used by `getEvents`. Never fabricates. */
async function getOfficialEvents(fixtureId: string): Promise<ProviderMatchEvent[]> {
  try {
    const data = await call<{ response: AfEventItem[] }>(`/fixtures/events?fixture=${fixtureId}`);
    const events = data.response ?? [];
    return events.map((e, idx): ProviderMatchEvent => {
      const minute = e.time?.elapsed ?? 0;
      const extra = e.time?.extra ?? null;
      const type = (e.type ?? "").trim();
      const detail = (e.detail ?? "").trim();
      const teamName = e.team?.name ?? "";
      const teamId = e.team?.id != null ? String(e.team.id) : null;
      const player = e.player?.name ?? null;
      const assist = e.assist?.name ?? null;
      return {
        minute,
        extraMinute: typeof extra === "number" ? extra : null,
        team: teamName,
        teamId,
        player,
        assist,
        type,
        detail,
        comments: e.comments ?? null,
        official: true,
        provider: "api_football",
        key: `${idx}-${minute}-${extra ?? 0}-${type}-${detail}-${teamId ?? ""}-${player ?? ""}`,
      };
    });
  } catch {
    // Never fabricate — return an empty timeline on provider failure.
    return [];
  }
}

interface AfFixturePlayerRow {
  player?: { id?: number; name?: string; photo?: string | null };
  statistics?: Array<{
    games?: {
      minutes?: number | null; number?: number | null; position?: string | null;
      rating?: string | number | null; captain?: boolean; substitute?: boolean;
    };
    shots?: { total?: number | null; on?: number | null };
    goals?: { total?: number | null; conceded?: number | null; assists?: number | null; saves?: number | null };
    passes?: { total?: number | null; key?: number | null; accuracy?: number | string | null };
    tackles?: { total?: number | null; blocks?: number | null; interceptions?: number | null };
    duels?: { total?: number | null; won?: number | null };
    dribbles?: { attempts?: number | null; success?: number | null };
    fouls?: { drawn?: number | null; committed?: number | null };
    cards?: { yellow?: number | null; red?: number | null };
    penalty?: { scored?: number | null; missed?: number | null };
  }>;
}
interface AfFixturePlayersBlock {
  team?: { id?: number; name?: string; logo?: string | null };
  players?: AfFixturePlayerRow[];
}

function nn(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = parseFloat(v.replace("%", ""));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Real Data Phase 4 — Official player match statistics fetcher.
 * Never fabricates: on error / empty response returns a payload with
 * `official: false` and null teams so the UI shows the lifecycle placeholder.
 */
async function getOfficialFixturePlayers(fixtureId: string): Promise<ProviderFixturePlayers> {
  const emptyPayload = (): ProviderFixturePlayers => ({
    fixtureId, official: false, provider: "api_football", home: null, away: null,
  });
  try {
    const data = await call<{ response: AfFixturePlayersBlock[] }>(
      `/fixtures/players?fixture=${fixtureId}`,
    );
    const blocks = data.response ?? [];
    if (blocks.length < 1) return emptyPayload();
    const mapTeam = (b?: AfFixturePlayersBlock): ProviderFixturePlayersTeam | null => {
      if (!b || !b.team) return null;
      const teamId = b.team.id != null ? String(b.team.id) : "?";
      const teamName = b.team.name ?? "?";
      const players: ProviderPlayerMatchStat[] = (b.players ?? []).map((row) => {
        const s = row.statistics?.[0] ?? {};
        const g = s.games ?? {};
        return {
          playerId: row.player?.id ?? 0,
          name: row.player?.name ?? "?",
          photo: row.player?.photo ?? null,
          teamId,
          teamName,
          shirtNumber: nn(g.number),
          position: g.position ?? null,
          captain: !!g.captain,
          substitute: !!g.substitute,
          minutes: nn(g.minutes),
          rating: nn(g.rating),
          goals: nn(s.goals?.total),
          assists: nn(s.goals?.assists),
          shots: nn(s.shots?.total),
          shotsOnTarget: nn(s.shots?.on),
          passes: nn(s.passes?.total),
          keyPasses: nn(s.passes?.key),
          passAccuracy: nn(s.passes?.accuracy),
          tackles: nn(s.tackles?.total),
          interceptions: nn(s.tackles?.interceptions),
          duels: nn(s.duels?.total),
          duelsWon: nn(s.duels?.won),
          dribbles: nn(s.dribbles?.attempts),
          dribblesSuccess: nn(s.dribbles?.success),
          foulsCommitted: nn(s.fouls?.committed),
          foulsDrawn: nn(s.fouls?.drawn),
          yellowCards: nn(s.cards?.yellow),
          redCards: nn(s.cards?.red),
          penaltiesScored: nn(s.penalty?.scored),
          penaltiesMissed: nn(s.penalty?.missed),
          saves: nn(s.goals?.saves),
          goalsConceded: nn(s.goals?.conceded),
          official: true,
          provider: "api_football",
        };
      });
      return { teamId, teamName, teamLogo: b.team.logo ?? null, players };
    };
    const home = mapTeam(blocks[0]);
    const away = mapTeam(blocks[1]);
    if (!home && !away) return emptyPayload();
    return { fixtureId, official: true, provider: "api_football", home, away };
  } catch {
    return emptyPayload();
  }
}